import { Image } from "antd";
import "../styles/MainProducts.scss";
import { useMemo } from "react";
import Assets from "../../../../../applications/assets";
import { ItemModel } from "../../../../../infrastructure/model/ProductItemModel";
import { useItemsContext } from "../../../../context/AppItemsContext";
import { useOrderList } from "../../../../context/OrderListContext";
import HomePageSection from "./HomePageSection/HomePageSection";
import HomeFooter from "./HomePageFooter";
import BestSellerSection from "./HomePageSection/BestSellerSection";
import { ITEM_TYPE_ENUM } from "../../../../../applications/constants/AppConst";
import Visible from "../../../../components/Visible";
import { HOME_PAGE_SECTION_NAME_ENUM } from "../../../../../applications/constants/AppData";
import ViewAllPage from "./HomePageSection/ViewAllPage";
import ViewAllBaseComp from "./HomePageSection/ViewAllBaseComp";
import FlashDealSection from "./HomePageSection/FlashDealSection";
import { useSaleInfoContext } from "../../../../context/AppSaleInfoContext";
import { useDebounce } from "../../../../hooks/useDebounce";
import { isEmpty } from "lodash";

interface MainProductsProps {
  filterProduct: string;
  setFilterProduct: (val: string) => void;
  selectedSectionId?: string;
  setSelectedSectionId: (val: string) => void;
}

function MainProducts(props: MainProductsProps) {
  const { filterProduct, selectedSectionId, setSelectedSectionId, setFilterProduct } = props;
  const { addToOrder } = useOrderList();
  const { saleInfo } = useSaleInfoContext();
  const { items, itemBestSeller } = useItemsContext();

  const activeItems: ItemModel[] = useMemo(() => {
    return items?.filter((t) => t.open_sale) || [];
  }, [items]);

  const newProductItems: ItemModel[] = useMemo(() => {
    return activeItems?.filter((t) => t.is_new) || [];
  }, [activeItems]);

  const hotSaleItems: ItemModel[] = useMemo(() => {
    return activeItems?.filter((t) => t.hot_sale) || [];
  }, [activeItems]);

  const coffeeItems: ItemModel[] = useMemo(() => {
    return activeItems?.filter((t) => t.type === ITEM_TYPE_ENUM.COFFEE_MATCHA_HOT_DRINK) || [];
  }, [activeItems]);

  const blackMilkTeaItems: ItemModel[] = useMemo(() => {
    return activeItems?.filter((t) => t.type === ITEM_TYPE_ENUM.BLACK_MILK_TEA) || [];
  }, [activeItems]);

  const oolongMilkTeaItems: ItemModel[] = useMemo(() => {
    return activeItems?.filter((t) => t.type === ITEM_TYPE_ENUM.OOLONG_MILK_TEA) || [];
  }, [activeItems]);

  const freshFruitItems: ItemModel[] = useMemo(() => {
    return activeItems?.filter((t) => t.type === ITEM_TYPE_ENUM.FRESH_FRUIT_JUICE) || [];
  }, [activeItems]);

  const nonCaffeinItems: ItemModel[] = useMemo(() => {
    return activeItems?.filter((t) => t.type === ITEM_TYPE_ENUM.NON_CAFFEIN) || [];
  }, [activeItems]);

  const smoothieItems: ItemModel[] = useMemo(() => {
    return activeItems?.filter((t) => t.type === ITEM_TYPE_ENUM.SMOOTHIE) || [];
  }, [activeItems]);

  const sessionDrinkItems: ItemModel[] = useMemo(() => {
    return activeItems?.filter((t) => t.type === ITEM_TYPE_ENUM.SEASON_DRINKS) || [];
  }, [activeItems]);

  const handleAddItemToOrder = (item: ItemModel) => {
    addToOrder({
      id: item.id,
      name: item.name,
      size: item?.size_mapping?.[0]?.size,
      price: item?.size_mapping?.[0]?.price,
      quantity: 1,
      image: item.image,
      type: item.type,
    });
  };

  const debouncedSearch = useDebounce(filterProduct, 400);

  const useSearchItems = useMemo(() => {
    if (!debouncedSearch?.trim()) return items;

    return items.filter((it) => it.name?.toLowerCase().includes(debouncedSearch.toLowerCase()));
  }, [debouncedSearch, items]);

  return (
    <div style={{ position: "relative" }} className="main-products">
      <Visible isVisible={!isEmpty(filterProduct?.trim())}>
        <ViewAllPage
          items={useSearchItems}
          title={
            useSearchItems?.length > 0 ? "Kết quả tìm kiếm" : "Không tìm thấy sản phẩm phù hợp"
          }
          onAdd={handleAddItemToOrder}
          onGoBack={() => setFilterProduct("")}
        />
      </Visible>
      <Visible isVisible={isEmpty(filterProduct?.trim())}>
        <Visible isVisible={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.NEW_PRODUCT_SECTION}>
          <ViewAllPage
            items={newProductItems}
            title="Sản phẩm mới"
            onAdd={handleAddItemToOrder}
            onGoBack={() => setSelectedSectionId("")}
          />
        </Visible>
        <Visible isVisible={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.FLASH_DEAL_SECTION}>
          <ViewAllBaseComp title="Quay lại trang chủ" onGoBack={() => setSelectedSectionId("")}>
            <FlashDealSection
              items={activeItems}
              onAdd={handleAddItemToOrder}
              sectionKey={HOME_PAGE_SECTION_NAME_ENUM.FLASH_DEAL_SECTION}
              onShowAll={setSelectedSectionId}
              isShowAll={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.FLASH_DEAL_SECTION}
              sales={saleInfo}
            />
          </ViewAllBaseComp>
        </Visible>
        <Visible isVisible={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.BEST_SELLER_SECTION}>
          <ViewAllBaseComp title="Quay lại trang chủ" onGoBack={() => setSelectedSectionId("")}>
            <BestSellerSection
              items={hotSaleItems}
              onAdd={handleAddItemToOrder}
              itemBestSeller={itemBestSeller}
              sectionKey={HOME_PAGE_SECTION_NAME_ENUM.BEST_SELLER_SECTION}
              onShowAll={setSelectedSectionId}
              isShowAll={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.BEST_SELLER_SECTION}
            />
          </ViewAllBaseComp>
        </Visible>
        <Visible
          isVisible={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.COFFEE_MATCHA_HOT_DRINK}
        >
          <ViewAllPage
            items={coffeeItems}
            title="Coffee mỗi ngày"
            onAdd={handleAddItemToOrder}
            onGoBack={() => setSelectedSectionId("")}
          />
        </Visible>
        <Visible
          isVisible={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.BLACK_MILK_TEA_SECTION}
        >
          <ViewAllPage
            items={blackMilkTeaItems}
            title="Trà sữa cho ngày ngọt ngào"
            onAdd={handleAddItemToOrder}
            onGoBack={() => setSelectedSectionId("")}
          />
        </Visible>
        <Visible
          isVisible={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.OOLONG_MILK_TEA_SECTION}
        >
          <ViewAllPage
            items={oolongMilkTeaItems}
            title="Ô long sữa"
            onAdd={handleAddItemToOrder}
            onGoBack={() => setSelectedSectionId("")}
          />
        </Visible>
        <Visible
          isVisible={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.FRESH_FRUIT_JUICE_SECTION}
        >
          <ViewAllPage
            items={freshFruitItems}
            title="Trà trái cây & nước ép"
            onAdd={handleAddItemToOrder}
            onGoBack={() => setSelectedSectionId("")}
          />
        </Visible>
        <Visible isVisible={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.NON_CAFFEIN_SECTION}>
          <ViewAllPage
            items={nonCaffeinItems}
            title="Non-caffein"
            onAdd={handleAddItemToOrder}
            onGoBack={() => setSelectedSectionId("")}
          />
        </Visible>
        <Visible isVisible={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.SMOOTHIE_SECTION}>
          <ViewAllPage
            items={smoothieItems}
            title="Đá xay, sinh tố"
            onAdd={handleAddItemToOrder}
            onGoBack={() => setSelectedSectionId("")}
          />
        </Visible>
        <Visible
          isVisible={selectedSectionId === HOME_PAGE_SECTION_NAME_ENUM.SEASON_DRINKS_SECTION}
        >
          <ViewAllPage
            items={sessionDrinkItems}
            title="Đồ uống theo mùa"
            onAdd={handleAddItemToOrder}
            onGoBack={() => setSelectedSectionId("")}
          />
        </Visible>

        <Visible isVisible={!selectedSectionId}>
          <section className="section_banner">
            <Image
              src={Assets.MENU_PAGE}
              style={{
                width: "100%",
                height: "100%",
              }}
            />
          </section>
          <div className="home-sections">
            {newProductItems?.length > 0 && (
              <HomePageSection
                title="Sản phẩm mới"
                icon={"🆕"}
                items={newProductItems || []}
                onAdd={handleAddItemToOrder}
                sectionKey={HOME_PAGE_SECTION_NAME_ENUM.NEW_PRODUCT_SECTION}
                onShowAll={setSelectedSectionId}
              />
            )}

            <FlashDealSection
              items={activeItems}
              onAdd={handleAddItemToOrder}
              sectionKey={HOME_PAGE_SECTION_NAME_ENUM.FLASH_DEAL_SECTION}
              onShowAll={setSelectedSectionId}
              sales={saleInfo}
            />
            {hotSaleItems?.length > 0 && (
              <BestSellerSection
                items={hotSaleItems}
                onAdd={handleAddItemToOrder}
                itemBestSeller={itemBestSeller}
                sectionKey={HOME_PAGE_SECTION_NAME_ENUM.BEST_SELLER_SECTION}
                onShowAll={setSelectedSectionId}
              />
            )}

            <HomePageSection
              title="Cà phê mỗi ngày"
              icon={"☕"}
              items={coffeeItems}
              onAdd={handleAddItemToOrder}
              sectionKey={HOME_PAGE_SECTION_NAME_ENUM.COFFEE_MATCHA_HOT_DRINK}
              onShowAll={setSelectedSectionId}
            />
            <HomePageSection
              title="Trà sữa cho ngày ngọt ngào"
              icon="🧋✨"
              items={blackMilkTeaItems}
              onAdd={handleAddItemToOrder}
              sectionKey={HOME_PAGE_SECTION_NAME_ENUM.BLACK_MILK_TEA_SECTION}
              onShowAll={setSelectedSectionId}
            />
          </div>
          <HomeFooter />
        </Visible>
      </Visible>
    </div>
  );
}
export default MainProducts;
